import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useQuote } from "../lib/quoteContext";
import { SITE } from "../lib/site";

const NAV = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "before-after", label: "Before & After" },
  { id: "testimonials", label: "Reviews" },
];

export default function Header() {
  const { openQuote } = useQuote();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => (ev) => {
    ev.preventDefault();
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const goHome = (ev) => {
    if (location.pathname === "/") {
      ev.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-sm"
          : "bg-[#DCEBFB]/0 backdrop-blur-0"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 py-4 flex items-center justify-between">
        <Link
          to="/"
          onClick={goHome}
          className="flex flex-col leading-tight"
          data-testid="header-logo"
        >
          <span className="font-serif text-[18px] sm:text-[20px] font-bold tracking-wide text-[#1A2B4A]">
            TINA'S CLEANING
          </span>
          <span className="text-[10px] tracking-[0.18em] text-[#5b6a85] mt-0.5">
            {SITE.tagline}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`/#${n.id}`}
              onClick={goToSection(n.id)}
              className="text-[14px] text-[#1A2B4A] hover:text-[#173B86] transition"
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => openQuote()}
            className="tc-btn-primary text-[13px] py-2.5 px-5"
            data-testid="header-cta-quote"
          >
            Get a Quote
          </button>
          <a
            href="https://wa.me/48572410075"
            target="_blank"
            rel="noopener noreferrer"
            className="tc-btn-outline text-[13px] py-2.5 px-5 inline-flex items-center gap-2"
            data-testid="header-cta-whatsapp"
          >
            <Phone className="w-3.5 h-3.5" /> Call me on WhatsApp
          </a>
        </div>

        <button
          className="lg:hidden p-2 text-[#1A2B4A]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden bg-white border-t border-[#e8ecf3] px-6 py-5 space-y-4"
          data-testid="header-mobile-menu"
        >
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`/#${n.id}`}
              onClick={goToSection(n.id)}
              className="block text-[15px] text-[#1A2B4A] py-1.5"
            >
              {n.label}
            </a>
          ))}
          <div className="pt-3 flex flex-col gap-3">
            <button
              onClick={() => {
                setMobileOpen(false);
                openQuote();
              }}
              className="tc-btn-primary w-full"
              data-testid="header-mobile-cta-quote"
            >
              Get a Quote
            </button>
            <a
              href="https://wa.me/48572410075"
              target="_blank"
              rel="noopener noreferrer"
              className="tc-btn-outline w-full text-center inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" /> Call me on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
